/**
 * Modelo: UsuarioSesion
 * 
 * Representa los datos de sesión del usuario autenticado.
 * Incluye información del empleado con sus relaciones.
 * 
 * @legacy PrestacionServiciosCGRHandler.sessionData() - retorna Tpsc_empleadosdto
 * @author Sura - Migración GWT a Spring Boot
 * @since 1.0
 */
export interface UsuarioSesion {
  // ==================== IDENTIFICACIÓN ====================
  
  /** Consecutivo del empleado (PK) */
  nmconsecutivo: number;
  
  /** DNI completo (tipo + número) del usuario */
  dni: string;
  
  /** Tipo de identificación (primer carácter del DNI) */
  tipoIdentificacion: string;
  
  /** Número de identificación (resto del DNI) */
  numeroIdentificacion: string;
  
  // ==================== DATOS PERSONALES ====================
  
  /** Código de persona (FK) */
  cdpersona: number;
  
  /** Nombre completo */
  nombreCompleto: string;
  
  /** Primer nombre */
  primerNombre: string;
  
  /** Segundo nombre */
  segundoNombre?: string;
  
  /** Primer apellido */
  primerApellido: string;
  
  /** Segundo apellido */
  segundoApellido?: string;
  
  // ==================== DATOS LABORALES ====================
  
  /** Email corporativo */
  emailCorporativo?: string;
  
  /** Cargo del empleado */
  cargo?: string;
  
  /** Profesión */
  profesion?: string;
  
  /** Usuario de red */
  usuarioRed?: string;
  
  // ==================== OFICINA ====================
  
  /** Consecutivo de la oficina */
  nmconsecOficina?: number;
  
  /** Código corto de la oficina */
  cdoficina?: string;
  
  /** Nombre de la oficina */
  nombreOficina?: string;
  
  // ==================== TIPO EMPLEADO ====================
  
  /** Código del tipo de empleado */
  cdtipoEmpleado?: string;
  
  /** Código del tipo de asesor (P=Prevención, R=Riesgos, I=Integral, etc.) */
  cdtipoAsesor?: string;

  /**
   * Código del tipo de asesor "real" — TPSC_TIPOS_ASESORES_REAL.CDTIPO_REAL.
   * Valores conocidos: P=PREVENCION, R=RIESGOS, L=LINEA_ATENCION.
   */
  cdtipoAsesorReal?: string;

  /** Descripción del tipo (Asesor, Administrador, etc.) */
  descripcionTipoEmpleado?: string;
  
  // ==================== PERFIL ====================
  
  /** Código del perfil */
  cdperfil?: string;
  
  /** Descripción del perfil */
  descripcionPerfil?: string;
  
  // ==================== COMPAÑÍA ====================
  
  /** Código de compañía */
  cdcompania?: string;
  
  // ==================== FLAGS ====================
  
  /** Está activo */
  activo: boolean;
  
  /** Es líder de equipo */
  lider: boolean;
  
  /** Disponible para programación */
  disponibleProgramacion: boolean;
  
  /** Usa programación en Outlook */
  programacionOutlook: boolean;
  
  /** Tiene asignación automática */
  asignacionAutomatica: boolean;

  /** Es empleado PYMES (SNPYMES = 'S'). Legacy: Tpsc_empleadosdto.getSnpymes() */
  pymes?: boolean;

  /**
   * Indica si el usuario es Director Técnico (cdtipoEmpleado = param CDTIPO_DIR_TECN).
   * Legacy: AsesoresSearchViewImpl.getDTO() L88-94 — el Director Técnico busca asesores
   * sin filtro de tipo de empleado. Calculado en backend.
   */
  esDirectorTecnico?: boolean;

  /**
   * Valor del parámetro general CDTIPO_EMP_ASES (tipo de empleado asesor) que el buscador
   * de asesores envía como cdtipoEmpleado cuando el usuario no es Director Técnico.
   * Vacío si el parámetro no está configurado.
   */
  cdtipoEmpAses?: string;
}

export interface EmpleadoPorDni {
  nmconsecutivo: number;
  cdpersona: number;
  activo: boolean;
}

/**
 * Obtiene las iniciales del usuario para avatar.
 */
export function getIniciales(usuario: UsuarioSesion): string {
  let iniciales = '';
  if (usuario.primerNombre) {
    iniciales += usuario.primerNombre.charAt(0);
  }
  if (usuario.primerApellido) {
    iniciales += usuario.primerApellido.charAt(0);
  }
  return iniciales.toUpperCase();
}

/**
 * Obtiene el nombre corto (primer nombre + primer apellido).
 */
export function getNombreCorto(usuario: UsuarioSesion): string {
  const partes: string[] = [];
  if (usuario.primerNombre) partes.push(usuario.primerNombre);
  if (usuario.primerApellido) partes.push(usuario.primerApellido);
  return partes.join(' ');
}
